import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { apiGet } from "../api/client";
import TownMultiSelect from "../components/TownMultiSelect";

interface SoldComp {
  listing_id: string;
  address?: string;
  town?: string;
  // Raw stored list price. On a sold row Zillow reports the sale price here,
  // so this is NOT the asking price — use ask_price for anything comparative.
  list_price?: number | null;
  // What the house was asking when it sold, derived from price history.
  ask_price?: number | null;
  original_ask_price?: number | null;
  sale_price?: number | null;
  sale_date?: string | null;
  sqft?: number | null;
  beds?: number | null;
  baths?: number | null;
  delta_pct?: number | null;
  delta_source?: "price_history" | "list_price" | null;
}

interface MarketStats {
  active: { count: number; median_price: number | null };
  sold: {
    count: number;
    median_price: number | null;
    avg_list_vs_sold_pct: number | null;
    delta_sample_count?: number;
    avg_days_on_market: number | null;
  };
}

const DAYS_OPTIONS = [30, 60, 90, 180, 365];

export default function SoldComps() {
  const [towns, setTowns] = useState<string[]>([]);
  const [days, setDays] = useState(90);

  const townsQ = useQuery<{ listings: { town: string }[] }>({
    queryKey: ["listings-towns"],
    queryFn: () => apiGet("/api/listings?status=all"),
  });
  const availableTowns = useMemo(() => {
    if (!townsQ.data) return [];
    const set = new Set<string>();
    for (const l of townsQ.data.listings) {
      if (l.town) set.add(l.town);
    }
    return Array.from(set).sort();
  }, [townsQ.data]);

  const townsParam = towns.length > 0 ? `&towns=${towns.map(encodeURIComponent).join(",")}` : "";

  const statsQ = useQuery<MarketStats>({
    queryKey: ["comps-stats", towns, days],
    queryFn: () => apiGet(`/api/comps/market-stats?days=${days}${townsParam}`),
  });
  const salesQ = useQuery<{ count: number; sales: SoldComp[] }>({
    queryKey: ["comps-sales", towns, days],
    queryFn: () => apiGet(`/api/comps/recent-sales?days=${days}&limit=200${townsParam}`),
  });

  const sales = salesQ.data?.sales ?? [];
  const chartData = useMemo(
    () =>
      sales
        .filter((s) => s.sale_date && s.delta_pct != null)
        .map((s) => ({ ...s, dateMs: new Date(s.sale_date as string).getTime() }))
        .sort((a, b) => a.dateMs - b.dateMs),
    [sales],
  );
  // Sold rows whose asking price we never captured. Their delta is unknown
  // rather than 0% — see the note rendered below the chart.
  const missingAsk = sales.filter((s) => s.delta_pct == null).length;

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>Sold Comps</h1>
      </div>

      <div
        className="panel"
        style={{ marginBottom: 12, padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}
      >
        <FilterField label="Town">
          <TownMultiSelect towns={availableTowns} selected={towns} onChange={setTowns} />
        </FilterField>
        <FilterField label="Window">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d}d
              </option>
            ))}
          </select>
        </FilterField>
      </div>

      <StatTiles stats={statsQ.data} loading={statsQ.isLoading} />

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Asking price vs. sale price</h3>
        {chartData.length === 0 ? (
          <p className="muted">
            {salesQ.isLoading
              ? "Loading…"
              : sales.length > 0
                ? "None of these sales have a captured asking price yet."
                : "No sold listings match this window yet."}
          </p>
        ) : (
          <>
            <DeltaChart data={chartData} />
            <div className="row" style={{ marginTop: 4 }}>
              <LegendDot color="var(--good)" label="Sold under asking" />
              <LegendDot color="var(--bad)" label="Sold over asking" />
            </div>
          </>
        )}
        {missingAsk > 0 && <MissingAskNote count={missingAsk} total={sales.length} />}
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Recent sales ({sales.length})</h3>
        {salesQ.isLoading && <p className="muted">Loading…</p>}
        {salesQ.error && <p className="tag-error">Failed to load sold comps.</p>}
        {!salesQ.isLoading && sales.length === 0 && <p className="muted">No sold listings match this window yet.</p>}
        {sales.length > 0 && <SalesTable sales={sales} />}
      </div>
    </>
  );
}

function MissingAskNote({ count, total }: { count: number; total: number }) {
  return (
    <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
      {count} of {total} sales have no asking price on record, so their delta shows as “—” rather than
      0%. Zillow reports a sold home's price as the <em>sale</em> price, so the ask can only come from
      the listing's price history — run <strong>Backfill extras</strong> in the extension popup to
      fetch it for past sales.
    </p>
  );
}

function StatTiles({ stats, loading }: { stats: MarketStats | undefined; loading: boolean }) {
  const sample = stats?.sold.delta_sample_count;
  const tiles = [
    { label: "Median sold price", value: fmtMoney(stats?.sold.median_price) },
    { label: "Median active list price", value: fmtMoney(stats?.active.median_price) },
    {
      label: "Avg ask-vs-sold delta",
      value: fmtPct(stats?.sold.avg_list_vs_sold_pct),
      // The delta average can only include sales with a known ask, which is
      // usually fewer than the sold count in the same window.
      sub: sample != null && stats ? `${sample} of ${stats.sold.count} sales` : undefined,
    },
    {
      label: "Avg days on market",
      value: stats?.sold.avg_days_on_market != null ? Math.round(stats.sold.avg_days_on_market).toString() : "—",
    },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
      {tiles.map((t) => (
        <div key={t.label} className="panel" style={{ marginBottom: 0, padding: 14 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {t.label}
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{loading ? "…" : t.value}</div>
          {t.sub && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {t.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

type ChartPoint = SoldComp & { dateMs: number };

function DeltaChart({ data }: { data: ChartPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="dateMs"
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          stroke="var(--border)"
          tick={{ fill: "var(--muted)", fontSize: 11 }}
        />
        <YAxis
          dataKey="delta_pct"
          unit="%"
          stroke="var(--border)"
          tick={{ fill: "var(--muted)", fontSize: 11 }}
        />
        <ReferenceLine y={0} stroke="var(--border)" strokeWidth={1} />
        <Tooltip content={<DeltaTooltip />} cursor={{ stroke: "var(--border)" }} />
        <Scatter
          data={data}
          shape={(props: { cx?: number; cy?: number; payload?: ChartPoint }) => {
            const positive = (props.payload?.delta_pct ?? 0) >= 0;
            return (
              <circle
                cx={props.cx}
                cy={props.cy}
                r={5}
                style={{ fill: positive ? "var(--bad)" : "var(--good)" }}
                stroke="var(--panel)"
                strokeWidth={2}
              />
            );
          }}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function DeltaTooltip({ active, payload }: { active?: boolean; payload?: { payload: ChartPoint }[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  const positive = (d.delta_pct ?? 0) >= 0;
  return (
    <div
      style={{
        background: "var(--panel)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 12,
        maxWidth: 220,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{d.address ?? "—"}</div>
      <div className="muted">
        {d.town ?? "?"} · {d.sale_date ?? "?"}
      </div>
      <div style={{ marginTop: 4 }}>
        Ask {fmtMoney(d.ask_price)} → Sold {fmtMoney(d.sale_price)}
      </div>
      {d.original_ask_price != null && d.original_ask_price !== d.ask_price && (
        <div className="muted" style={{ fontSize: 11 }}>
          originally listed {fmtMoney(d.original_ask_price)}
        </div>
      )}
      <div style={{ color: positive ? "var(--bad)" : "var(--good)", fontWeight: 600 }}>{fmtPct(d.delta_pct)}</div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 16 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
      <span className="muted" style={{ fontSize: 12 }}>
        {label}
      </span>
    </span>
  );
}

type SortKey = "sale_date" | "sale_price" | "delta_pct" | "ask_price";

function SalesTable({ sales }: { sales: SoldComp[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("sale_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const sorted = useMemo(() => {
    const copy = [...sales];
    copy.sort((a, b) => {
      if (sortKey === "sale_date") {
        const av = a.sale_date ?? "";
        const bv = b.sale_date ?? "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [sales, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <table>
      <thead>
        <tr>
          <SortableHeader label="Sale date" sortKey="sale_date" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <th>Address</th>
          <th>Town</th>
          <th>Beds/Baths</th>
          <th>Sqft</th>
          <SortableHeader label="Asking price" sortKey="ask_price" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <SortableHeader label="Sale price" sortKey="sale_price" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <SortableHeader label="Delta" sortKey="delta_pct" active={sortKey} dir={sortDir} onClick={toggleSort} />
        </tr>
      </thead>
      <tbody>
        {sorted.map((s) => {
          const known = s.delta_pct != null;
          const positive = (s.delta_pct ?? 0) >= 0;
          return (
            <tr key={s.listing_id}>
              <td className="muted">{s.sale_date ?? "—"}</td>
              <td>
                <Link to={`/listings/${s.listing_id}`}>{s.address ?? s.listing_id}</Link>
              </td>
              <td className="muted">{s.town ?? "?"}</td>
              <td className="muted">
                {s.beds ?? "?"} / {s.baths ?? "?"}
              </td>
              <td className="muted">{s.sqft != null ? s.sqft.toLocaleString() : "?"}</td>
              <td>
                {fmtMoney(s.ask_price)}
                {s.original_ask_price != null && s.original_ask_price !== s.ask_price && (
                  <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                    from {fmtMoney(s.original_ask_price)}
                  </span>
                )}
              </td>
              <td>{fmtMoney(s.sale_price)}</td>
              <td
                title={known ? undefined : "No asking price on record for this sale"}
                style={{
                  color: known ? (positive ? "var(--bad)" : "var(--good)") : "var(--muted)",
                  fontWeight: known ? 600 : 400,
                }}
              >
                {fmtPct(s.delta_pct)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SortableHeader({
  label,
  sortKey,
  active,
  dir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: "asc" | "desc";
  onClick: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th onClick={() => onClick(sortKey)} style={{ cursor: "pointer", userSelect: "none" }}>
      {label}
      {isActive ? (dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="muted" style={{ fontSize: 11 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return "$" + n.toLocaleString();
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}
